import classNames from "classnames";
import EmptyState from "@/components/EmptyState";
import { RowHeader, RowLink, TableCard, TableCardRow } from "@/components/Table";
import type { MaterialNames } from "@/components/material_symbol_names";
import { type Path, useNavigate } from "@/router";
import type { DashboardMemberSignal, DashboardMemberSignalGroup } from "../_utils/dashboardSignal";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import { DASHBOARD_PANEL_SHELL_CLASS, DASHBOARD_TABLE_CONTENT_CELL } from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";
import DashboardEmptyRow from "./DashboardEmptyRow";
import DashboardTableSkeleton from "./DashboardTableSkeleton";

const COLUMN_COUNT = 4;
const AVATAR_CELL = "w-14 px-4 py-2 align-middle";
const ACTIVITY_CELL =
  "w-44 min-w-0 whitespace-normal break-words align-middle py-3 text-left text-body-medium text-sys-brand-on-surface-variant";
const MEMBER_LIST_PATH = "/people?programTypes%5B%5D=MEMBER" as Path;
const ASSOCIATE_LIST_PATH = "/people?programTypes%5B%5D=ASSOCIATE" as Path;

export type DashboardSignalTableProps = {
  title: string;
  icon: MaterialNames;
  testId: string;
  tableTestId: string;
  tableAriaLabel: string;
  groupTestIdPrefix: string;
  rowTestIdPrefix: string;
  personLabel: string;
  groups: DashboardMemberSignalGroup[];
  loading?: boolean;
  error?: boolean;
  unavailable?: boolean;
  viewAll?: {
    label: string;
    to: Path;
    testId: string;
  };
};

function InitialsAvatar({ initials }: { initials: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 min-w-10 items-center justify-center rounded-full bg-sys-brand-secondary-container"
    >
      <span className="text-label-large text-sys-brand-on-surface-variant">{initials}</span>
    </div>
  );
}

function SignalDetails({ member }: { member: DashboardMemberSignal }) {
  return (
    <>
      {member.role ? <div className="text-body-medium">{member.role}</div> : null}
      {member.facility ? (
        <div className="text-body-small text-sys-brand-on-surface-variant">{member.facility}</div>
      ) : null}
    </>
  );
}

function personListPath(personLabel: string): Path {
  if (personLabel === "associate") {
    return ASSOCIATE_LIST_PATH;
  }

  if (personLabel === "member") {
    return MEMBER_LIST_PATH;
  }

  return "/people" as Path;
}

function SignalRow({
  member,
  personLabel,
  rowTestIdPrefix
}: {
  member: DashboardMemberSignal;
  personLabel: string;
  rowTestIdPrefix: string;
}) {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const openPerson = member.href ? () => navigate(member.href as Path) : undefined;

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`${rowTestIdPrefix}-${member.id}`}
      onClick={openPerson}
      ariaLabel={openPerson ? `Open ${personLabel}: ${member.name}` : undefined}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 gap-3">
          <InitialsAvatar initials={member.initials} />
          <div className="min-w-0 flex-1">
            <div className="break-words text-body-medium text-sys-brand-on-surface">{member.name}</div>
            <SignalDetails member={member} />
            <div className="pt-1 break-words text-body-small text-sys-brand-on-surface-variant">
              {member.activityLabel}
            </div>
          </div>
        </div>
      }
    >
      <td className={AVATAR_CELL}>
        <InitialsAvatar initials={member.initials} />
      </td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "w-[28%] min-w-0 max-w-[12rem] break-words")}>
        {member.name}
      </td>
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "min-w-0 break-words")}>
        <SignalDetails member={member} />
      </td>
      <td className={ACTIVITY_CELL}>{member.activityLabel}</td>
    </TableCardRow>
  );
}

function SignalGroup({
  group,
  groupTestIdPrefix,
  personLabel,
  rowTestIdPrefix
}: {
  group: DashboardMemberSignalGroup;
  groupTestIdPrefix: string;
  personLabel: string;
  rowTestIdPrefix: string;
}) {
  return (
    <>
      <RowHeader
        label={group.label}
        count={group.count ?? group.rows.length}
        columnCount={COLUMN_COUNT}
        rowTestId={`${groupTestIdPrefix}-${group.id}`}
      />
      {group.rows.length > 0 ? (
        group.rows.map((member) => (
          <SignalRow key={member.id} member={member} personLabel={personLabel} rowTestIdPrefix={rowTestIdPrefix} />
        ))
      ) : (
        <DashboardEmptyRow
          columnCount={COLUMN_COUNT}
          message={`No ${personLabel}s in this section.`}
          rowTestId={`${groupTestIdPrefix}-${group.id}-empty`}
        />
      )}
    </>
  );
}

export default function DashboardSignalTable({
  title,
  icon,
  testId,
  tableTestId,
  tableAriaLabel,
  groupTestIdPrefix,
  rowTestIdPrefix,
  personLabel,
  groups,
  loading = false,
  error = false,
  unavailable = false,
  viewAll
}: DashboardSignalTableProps) {
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const personLabelTitle = `${personLabel.charAt(0).toUpperCase()}${personLabel.slice(1)}`;
  const showViewAll = !loading && !error && !unavailable;
  const hasMoreGroups = groups.some((group) => group.hasMore);

  return (
    <TableCard
      title={title}
      icon={icon}
      testId={testId}
      shellClassName={DASHBOARD_PANEL_SHELL_CLASS}
      bodyClassName={classNames(DASHBOARD_TABLE_CARD_BODY_CLASS, "overflow-x-auto")}
    >
      <table
        className={getDashboardPreviewTableClassName(isStackedTableRows)}
        data-testid={tableTestId}
        aria-label={tableAriaLabel}
      >
        {isStackedTableRows ? null : (
          <colgroup>
            <col className="w-14" />
            <col className="w-[28%]" />
            <col />
            <col className="w-44" />
          </colgroup>
        )}
        <tbody>
          {loading ? (
            <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
          ) : unavailable ? (
            <tr data-testid={`${testId}-unavailable-row`}>
              <td colSpan={COLUMN_COUNT}>
                <EmptyState
                  iconName="lock"
                  title={`Career Services ${personLabelTitle} Dashboard Unavailable`}
                  caption={`You do not have permission to view Career Services ${personLabel} activity.`}
                />
              </td>
            </tr>
          ) : error ? (
            <tr data-testid={`${testId}-error-row`}>
              <td colSpan={COLUMN_COUNT}>
                <EmptyState
                  iconName="error"
                  title={`Unable to Load ${personLabelTitle}s`}
                  caption="Try refreshing the page. If the problem continues, contact support."
                />
              </td>
            </tr>
          ) : groups.length > 0 ? (
            groups.map((group) => (
              <SignalGroup
                key={group.id}
                group={group}
                groupTestIdPrefix={groupTestIdPrefix}
                personLabel={personLabel}
                rowTestIdPrefix={rowTestIdPrefix}
              />
            ))
          ) : (
            <DashboardEmptyRow
              columnCount={COLUMN_COUNT}
              message={`No ${personLabel}s in this section.`}
              rowTestId={`${testId}-empty-row`}
            />
          )}
          {showViewAll && !viewAll && hasMoreGroups ? (
            <RowLink columnCount={COLUMN_COUNT} to={personListPath(personLabel)} testId={`${testId}-view-all`}>
              View all {personLabel}s
            </RowLink>
          ) : null}
          {showViewAll && viewAll ? (
            <RowLink columnCount={COLUMN_COUNT} to={viewAll.to} testId={viewAll.testId}>
              {viewAll.label}
            </RowLink>
          ) : null}
        </tbody>
      </table>
    </TableCard>
  );
}
